import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { getBaileysServerURL } from "@/services/whatsApp/utils";
import { Loader2, CheckCircle, Send, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getTemplates } from "@/services/templateService";
import { Template } from "@/types/whatsApp";

export const CustomerConnect = () => {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("Willkommen bei unserem Service!");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const { toast } = useToast();
  const wassiServerURL = getBaileysServerURL();
  const wassiApiKey = "23257599add7ae89a708a6ccd4a90cb720eee80098e96a1ef4b73293bdbe7d5c55a76402569376b1";

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const loadedTemplates = await getTemplates();
      setTemplates(loadedTemplates);
      setIsLoadingTemplates(false);
    } catch (error) {
      console.error("Fehler beim Laden der Templates:", error);
      toast({
        title: "Fehler",
        description: "Templates konnten nicht geladen werden",
        variant: "destructive"
      });
      setIsLoadingTemplates(false);
    }
  };

  const validatePhoneNumber = (number: string) => {
    // Basic validation - can be enhanced
    return number.replace(/\s+/g, "").match(/^\+?[0-9]{10,15}$/);
  };

  const formatPhoneNumber = (number: string) => {
    // Format for WhatsApp API: should be in the format "country code + number" without any symbols
    // Example: 491234567890 for a German number
    let formatted = number.replace(/\s+/g, "");
    
    // If the number doesn't start with a + and doesn't have a country code, add German code
    if (!formatted.startsWith("+") && !formatted.startsWith("49")) {
      // If it starts with a 0, replace it with the country code
      if (formatted.startsWith("0")) {
        formatted = "49" + formatted.substring(1);
      } else {
        formatted = "49" + formatted;
      }
    } else if (formatted.startsWith("+")) {
      // Remove the + symbol
      formatted = formatted.substring(1);
    }
    
    return formatted;
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      
      // Replace placeholders with customer name if available
      let content = template.content;
      if (name && content.includes("{{name}}")) {
        content = content.replace(/{{name}}/g, name);
      }
      
      setMessage(content);
    }
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePhoneNumber(phone)) {
      toast({
        title: "Ungültige Telefonnummer",
        description: "Bitte geben Sie eine gültige Telefonnummer ein",
        variant: "destructive",
      });
      return;
    }
    
    const formattedNumber = formatPhoneNumber(phone);
    setIsConnecting(true);
    
    try {
      // Call wassi.chat API to register customer with API key
      const response = await fetch(`${wassiServerURL}/api/customer/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${wassiApiKey}`
        },
        body: JSON.stringify({
          phone: formattedNumber,
          name: name,
          welcomeMessage: message
        }),
      });
      
      console.log("Wassi.chat response status:", response.status);
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error("Error data:", errorData);
        throw new Error(errorData || "Fehler bei der Verbindung mit wassi.chat");
      }
      
      setIsConnected(true);
      toast({
        title: "Erfolg",
        description: "Kunde erfolgreich mit wassi.chat verbunden"
      });
    } catch (error) {
      console.error("Fehler bei der Verbindung mit wassi.chat:", error);
      toast({
        title: "Verbindung fehlgeschlagen",
        description: error instanceof Error ? error.message : "Fehler bei der Verbindung mit wassi.chat",
        variant: "destructive"
      });
    } finally {
      setIsConnecting(false);
    }
  };
  
  const handleSendMessage = async () => {
    if (!message.trim() || !isConnected) return;
    
    setIsSending(true);
    try {
      const formattedNumber = formatPhoneNumber(phone);
      
      const response = await fetch(`${wassiServerURL}/api/send-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${wassiApiKey}`
        },
        body: JSON.stringify({
          to: formattedNumber,
          text: message
        }),
      });
      
      if (!response.ok) {
        throw new Error("Nachricht konnte nicht gesendet werden");
      }
      
      toast({
        title: "Nachricht gesendet",
        description: "Ihre Nachricht wurde erfolgreich versendet"
      });
      
      // Keep the connection but clear the message field if not using a template
      if (!selectedTemplate) {
        setMessage("");
      }
    } catch (error) {
      console.error("Fehler beim Senden der Nachricht:", error);
      toast({
        title: "Senden fehlgeschlagen",
        description: "Nachricht konnte nicht gesendet werden",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Kundenzugang</CardTitle>
        <CardDescription>
          Verbinden Sie einen Kunden mit dem wassi.chat System und senden Sie eine Begrüßungsnachricht.
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleConnect} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Telefonnummer (mit Landesvorwahl)</Label>
            <Input 
              id="phone" 
              placeholder="+49 123 4567890" 
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={isConnected}
            />
            <p className="text-xs text-gray-400">
              Geben Sie die Telefonnummer mit Landesvorwahl ein (z.B. +49 für Deutschland)
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name">Name des Kunden</Label>
            <Input 
              id="name" 
              placeholder="Max Mustermann" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isConnected}
            />
          </div>
          
          {isConnected && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="template">Nachrichtenvorlage</Label>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={loadTemplates}
                  disabled={isLoadingTemplates}
                  className="h-8 px-2"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoadingTemplates ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Vorlage auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="message">Nachricht</Label>
            <Input 
              id="message" 
              placeholder="Willkommen bei unserem Service!" 
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                // Clear selected template when message is manually edited
                if (selectedTemplate) setSelectedTemplate("");
              }}
            />
          </div>
          
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
            {!isConnected ? (
              <Button type="submit" className="w-full" disabled={isConnecting || !phone || !name}>
                {isConnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verbindung wird hergestellt...
                  </>
                ) : (
                  "Mit wassi.chat verbinden"
                )}
              </Button>
            ) : (
              <>
                <Button type="button" className="w-full bg-green-600 hover:bg-green-700" disabled>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Verbunden
                </Button>
                
                <Button 
                  type="button" 
                  onClick={handleSendMessage} 
                  className="w-full"
                  disabled={isSending || !message.trim()}
                >
                  {isSending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Nachricht senden
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
